import React, {useEffect} from 'react';

import {StyledGrid} from 'presentation/components';
import {useAccountInformation, useMediaQueries} from 'presentation/pages/private';
import {translations} from 'presentation/translations-new';

import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Divider,
	Typography,
	useTheme,
} from '@mui/material';
import {FullscreenLoading} from 'presentation/components/Loading/FullscreenLoading';
import {Loading} from 'presentation/components/Loading/Loading';
import {FeaturesEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import Icon from 'presentation/icons';
import {RenderPlans} from './components/RenderPlans';
import {usePlanSelection} from './hooks/usePlanSelection';

const {loading, activeContracts, inactiveContracts} = translations['pt-br'].commons;

export function PlanSelection() {
	const {
		handleSelectPlan,
		planCardSelectedState,
		shouldAutoSelectUniquePlan,
		shouldAutoSelectUniquePlanAndBeneficiary,
		shouldLogout,
		showLoadingScreen,
		uniquePlan,
		beneficiariesList,
		isInInitialLoginFlow,
		plansList,
	} = usePlanSelection();
	const {isTablet} = useMediaQueries();
	const {handleLogout} = useAccountInformation();
	const theme = useTheme();

	const {isEnabled: isNewUIEntryFlowIsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_NEW_UI_ENTRY_FLOW,
	);

	useEffect(() => {
		if (shouldAutoSelectUniquePlanAndBeneficiary && uniquePlan) {
			handleSelectPlan({
				plan: uniquePlan,
				beneficiary: beneficiariesList?.[0],
			});
		}
		if (shouldAutoSelectUniquePlan && uniquePlan) {
			handleSelectPlan({
				plan: uniquePlan,
			});
		}
	}, [
		beneficiariesList,
		uniquePlan,
		shouldAutoSelectUniquePlanAndBeneficiary,
		shouldAutoSelectUniquePlan,
	]);

	if (showLoadingScreen) {
		if (isInInitialLoginFlow) {
			return <FullscreenLoading text={loading} />;
		}

		return <Loading text={loading} boxProps={{flex: 1}} />;
	}

	if (shouldLogout) {
		return handleLogout();
	}

	if (plansList) {
		const activePlans = plansList.filter(plan => !plan?.dtCancelamento);
		const canceledPlans = plansList.filter(plan => !!plan?.dtCancelamento);
		const sortedPlans = [...plansList].sort((firstPlan, secondPlan) => {
			const firstPlanHasCancelDate = firstPlan?.dtCancelamento ? 1 : 0;
			const secondPlanHasCancelDate = secondPlan?.dtCancelamento ? 1 : 0;
			return firstPlanHasCancelDate - secondPlanHasCancelDate;
		});

		return (
			<StyledGrid.Root padding={isTablet ? 2 : 4} spacing={isTablet ? 2 : 3}>
				{!isNewUIEntryFlowIsEnabled ? (
					<RenderPlans
						plans={sortedPlans}
						planCardSelectedState={planCardSelectedState}
						handleSelectPlan={handleSelectPlan}
					/>
				) : null}

				{isNewUIEntryFlowIsEnabled && activePlans.length > 0 ? (
					<>
						<StyledGrid.Item xs={12}>
							<Typography variant="body2" color="text.secondary" fontWeight={600}>
								{activeContracts}
							</Typography>
						</StyledGrid.Item>

						<RenderPlans
							plans={activePlans}
							planCardSelectedState={planCardSelectedState}
							handleSelectPlan={handleSelectPlan}
						/>
					</>
				) : null}

				{isNewUIEntryFlowIsEnabled && canceledPlans.length > 0 ? (
					<StyledGrid.Item xs={12}>
						{activePlans.length > 0 ? <Divider sx={{mb: isTablet ? 2 : 3}} /> : null}
						<Accordion defaultExpanded={activePlans.length === 0} elevation={0}>
							<AccordionSummary
								expandIcon={
									<Icon
										name="expand-more-filled"
										color={theme.palette.primary.main}
										size={20}
									/>
								}>
								<Typography variant="body2" color="text.secondary" fontWeight={600}>
									{inactiveContracts}
								</Typography>
							</AccordionSummary>

							<AccordionDetails>
								<StyledGrid.Root spacing={2}>
									<RenderPlans
										plans={canceledPlans}
										planCardSelectedState={planCardSelectedState}
										handleSelectPlan={handleSelectPlan}
									/>
								</StyledGrid.Root>
							</AccordionDetails>
						</Accordion>
					</StyledGrid.Item>
				) : null}
			</StyledGrid.Root>
		);
	}
}
